/**
 * 表单草稿持久化(localStorage)
 *
 * 用法:
 *   saveDraft('supervision-log', form.getFieldsValue())   // 防抖调用
 *   loadDraft('supervision-log')                          // 返回 null | values
 *   clearDraft('supervision-log')                         // 提交成功时调用
 *   hasDraft('supervision-log')                           // UI 提示用
 *
 * 行为:
 * - 7 天未使用自动清理
 * - 写满或异常静默吞掉(不影响主流程)
 * - dayjs 对象 ↔ ISO 字符串自动转换(否则 JSON 序列化后丢失)
 * - 跨标签页/刷新都生效
 *
 * 草稿 key 由调用方决定,推荐包含业务名 + 项目 id,避免跨项目串台:
 *   saveDraft(`supervision-log-${projectId}`, values)
 */
import dayjs from 'dayjs'

const PREFIX = 'pm_draft_'
const TTL_MS = 7 * 24 * 60 * 60 * 1000

// YYYY-MM-DD 或完整 ISO 时间,认作日期字段
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:?\d{2})?)?$/

function safeStorage(fn, fallback) {
  try { return fn() } catch { return fallback }
}

function serializeDates(value) {
  if (value == null) return value
  if (dayjs.isDayjs(value)) return value.toISOString()
  if (Array.isArray(value)) return value.map(serializeDates)
  if (typeof value === 'object') {
    const out = {}
    for (const k in value) out[k] = serializeDates(value[k])
    return out
  }
  return value
}

function hasCorruption(value) {
  // 旧版本序列化把 dayjs 变成 {},会触发 DatePicker 崩溃
  if (value && typeof value === 'object' && !Array.isArray(value) && !dayjs.isDayjs(value)) {
    if (Object.keys(value).length === 0) return true
    // dayjs 内部对象 {$d, $y, $M, ...} 也会导致 DatePicker 崩溃
    if ('$d' in value || '$y' in value) return true
    for (const k in value) if (hasCorruption(value[k])) return true
  }
  if (Array.isArray(value)) {
    for (const v of value) if (hasCorruption(v)) return true
  }
  return false
}

function deserializeDates(value) {
  if (typeof value === 'string' && ISO_DATE_RE.test(value)) {
    const d = dayjs(value)
    if (d.isValid()) return d
  }
  if (Array.isArray(value)) return value.map(deserializeDates)
  if (value && typeof value === 'object') {
    const out = {}
    for (const k in value) out[k] = deserializeDates(value[k])
    return out
  }
  return value
}

export function saveDraft(key, data) {
  safeStorage(() => {
    const payload = serializeDates(data)
    localStorage.setItem(PREFIX + key, JSON.stringify({ data: payload, savedAt: Date.now() }))
  })
}

export function loadDraft(key) {
  return safeStorage(() => {
    const raw = localStorage.getItem(PREFIX + key)
    if (!raw) return null
    const { data, savedAt } = JSON.parse(raw)
    if (!savedAt || Date.now() - savedAt > TTL_MS) {
      localStorage.removeItem(PREFIX + key)
      return null
    }
    // 旧版本残留的 {} 坏数据,清掉整个 key 避免 DatePicker 崩溃
    if (hasCorruption(data)) {
      localStorage.removeItem(PREFIX + key)
      return null
    }
    return deserializeDates(data)
  }, null)
}

export function clearDraft(key) {
  safeStorage(() => localStorage.removeItem(PREFIX + key))
}

export function hasDraft(key) {
  return loadDraft(key) != null
}

// 全局清理:应用启动时扫描所有含 dayjs 内部对象({$d,$y})的损坏草稿
export function cleanupCorruptedDrafts() {
  safeStorage(() => {
    const keys = []
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i)
      if (k && k.startsWith(PREFIX)) keys.push(k)
    }
    for (const k of keys) {
      try {
        const raw = JSON.parse(localStorage.getItem(k) || '{}')
        if (hasCorruption(raw?.data)) {
          localStorage.removeItem(k)
          // eslint-disable-next-line no-console
          if (import.meta.env.DEV) console.warn('draftStorage: removed corrupted draft', k)
        }
      } catch { localStorage.removeItem(k) }
    }
  })
}

/**
 * 通用防抖(返回的 fn.cancel() 取消待执行任务,用于组件卸载时清理)
 */
export function debounce(fn, delay = 300) {
  let timer
  const debounced = (...args) => {
    clearTimeout(timer)
    timer = setTimeout(() => fn(...args), delay)
  }
  debounced.cancel = () => {
    if (timer) {
      clearTimeout(timer)
      timer = null
    }
  }
  return debounced
}
